import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, Injector } from '@angular/core';
import { Observable, Subject, concat, of, debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs';


export interface SelectItem {
  id?: string | number;
  name?: string;
  label?: string;
  centralCostNumber?: string;
  [key: string]: unknown;
}

export interface FilterConfig {
  key: string;                // nome do campo que será emitido
  label: string;              // label exibido
  type: 'text' | 'select' | 'date';
  serviceName?: string;       // nome do service a ser injetado dinamicamente (para select)
  apiMethod?: string;         // nome do método do service que retorna os dados do select
  bindLabel?: string;         // campo para exibir no ng-select
  bindValue?: string;         // valor emitido no ng-select
  placeholder?: string;       // placeholder para o campo
  typeahead?: boolean;        // se deve usar typeahead para select
  items?: Observable<SelectItem[]> | SelectItem[]; // dados estáticos ou observables para o select
  service?: unknown;          // service diretamente injetado
  labelColor?: string;        // cor da label (ex: '#6F1568')
}

export interface FilterValue {
  key: string;
  value: string | number | Date | SelectItem | null;
}

@Component({
  selector: 'app-dynamic-filter',
  templateUrl: './dynamic-filter.component.html',
  styleUrls: ['./dynamic-filter.component.scss']
})
export class DynamicFilterComponent implements OnInit, OnDestroy {
  @Input() filtersConfig: FilterConfig[] = [];
  @Input() initialValues: { [key: string]: string | number | Date | SelectItem | null } = {};
  @Input() defaultLabelColor = 'var(--default-label-color, #495057)'; // Cor padrão para as labels usando variável CSS de tema
  @Output() filterChange = new EventEmitter<FilterValue[]>();

  filterValues: { [key: string]: string | number | Date | SelectItem | null } = {};
  selectData: { [key: string]: Observable<SelectItem[]> } = {};
  selectLoading: { [key: string]: boolean } = {};
  selectInput$: { [key: string]: Subject<string> } = {};

  constructor(private injector: Injector) {}

  ngOnInit(): void {
    this.initializeFilters();
    this.setInitialValues();
  }

  ngOnDestroy(): void {
    Object.values(this.selectInput$).forEach(subject => subject.complete());
  }

  private initializeFilters(): void {
    this.filtersConfig.forEach(filter => {
      this.filterValues[filter.key] = null;
      
      if (filter.type === 'select') {
        if (filter.items) {
          // Usar dados estáticos ou observables fornecidos
          this.selectData[filter.key] = Array.isArray(filter.items) ? of(filter.items) : filter.items;
        } else if (filter.service) {
          // Usar service diretamente injetado
          this.initializeSelectFilterWithService(filter);
        } else if (filter.serviceName && filter.apiMethod) {
          // Usar injeção dinâmica de service
          this.initializeSelectFilter(filter);
        }
      }
    });
  }

  private initializeSelectFilterWithService(filter: FilterConfig): void {
    this.selectLoading[filter.key] = false;
    this.selectInput$[filter.key] = new Subject<string>();

    try {
      const service = filter.service;
      
      if (filter.typeahead) {
        // Para selects com typeahead
        this.selectData[filter.key] = concat(
          of([]),
          this.selectInput$[filter.key].pipe(
            debounceTime(500),
            distinctUntilChanged(),
            tap(() => this.selectLoading[filter.key] = true),
            switchMap((value) => service[filter.apiMethod || ''](value) as Observable<SelectItem[]>),
            tap(() => this.selectLoading[filter.key] = false)
          )
        );
      } else {
        // Para selects simples
        this.selectData[filter.key] = service[filter.apiMethod || '']() as Observable<SelectItem[]>;
      }
    } catch (error) {
      console.error(`Erro ao usar service para ${filter.key}:`, error);
      this.selectData[filter.key] = of([]);
    }
  }

  private initializeSelectFilter(filter: FilterConfig): void {
    this.selectLoading[filter.key] = false;
    this.selectInput$[filter.key] = new Subject<string>();

    try {
      // Injeção dinâmica do service usando o token correto
      let service: unknown;
      
      // Mapeamento de nomes de services para tokens
      const serviceTokens: { [key: string]: string } = {
        'BranchsService': 'BranchsService',
        'AreaStore': 'AreaStore'
      };
      
      const token = serviceTokens[filter.serviceName || ''];
      if (token) {
        service = this.injector.get(token);
      } else {
        service = this.injector.get(filter.serviceName as string);
      }
      
      if (filter.typeahead) {
        // Para selects com typeahead
        this.selectData[filter.key] = concat(
          of([]),
          this.selectInput$[filter.key].pipe(
            debounceTime(500),
            distinctUntilChanged(),
            tap(() => this.selectLoading[filter.key] = true),
            switchMap((value) => service[filter.apiMethod || ''](value) as Observable<SelectItem[]>),
            tap(() => this.selectLoading[filter.key] = false)
          )
        );
      } else {
        // Para selects simples
        this.selectData[filter.key] = service[filter.apiMethod || '']() as Observable<SelectItem[]>;
      }
    } catch (error) {
      console.error(`Erro ao injetar service ${filter.serviceName}:`, error);
      this.selectData[filter.key] = of([]);
    }
  }

  private setInitialValues(): void {
    if (this.initialValues) {
      Object.keys(this.initialValues).forEach(key => {
        if (Object.prototype.hasOwnProperty.call(this.filterValues, key)) {
          this.filterValues[key] = this.initialValues[key];
        }
      });
    }
  }

  onFilterChange(key: string, value: string | number | Date | SelectItem | null): void {
    this.filterValues[key] = value;
    this.emitFilterChange();
  }

  onSelectInputChange(key: string, value: string): void {
    if (this.selectInput$[key]) {
      this.selectInput$[key].next(value);
    }
  }

  private emitFilterChange(): void {
    const filterArray: FilterValue[] = Object.keys(this.filterValues)
      .map(key => ({
        key,
        value: this.filterValues[key]
      }))
      .filter(filter => filter.value !== null && filter.value !== undefined && filter.value !== '');

    this.filterChange.emit(filterArray);
  }

  clearFilters(): void {
    Object.keys(this.filterValues).forEach(key => {
      this.filterValues[key] = null;
    });
    this.emitFilterChange();
  }

  getFilterValue(key: string): string | number | Date | SelectItem | null {
    return this.filterValues[key];
  }

  getDisplayValue(item: SelectItem, filter: FilterConfig): string {
    if (!item) return '';
    
    if (filter.bindLabel) {
      return String(item[filter.bindLabel] || '');
    }
    
    if (item.name && item.centralCostNumber) {
      return `${item.name} - ${item.centralCostNumber}`;
    }
    
    return item.name || item.label || item.toString();
  }

  getSelectItems(filter: FilterConfig): Observable<SelectItem[]> {
    if (filter.items) {
      return Array.isArray(filter.items) ? of(filter.items) : filter.items;
    }
    
    if (this.selectData[filter.key]) {
      return this.selectData[filter.key];
    }
    
    return of([]);
  }
}
